export type RecoveryRow = Record<string, unknown>;

export interface RecoveryRowsInput {
  userId: string;
  companyId: string;
  users: RecoveryRow[];
  companies: RecoveryRow[];
  templates: RecoveryRow[];
  fields: RecoveryRow[];
  checklists: RecoveryRow[];
  values: RecoveryRow[];
  markingImages: RecoveryRow[];
  pendingOperations: RecoveryRow[];
  pendingImages: RecoveryRow[];
  pendingDeletions: RecoveryRow[];
}

const matches = (value: unknown, expected: string): boolean => String(value ?? '') === expected;

const parsePendingData = (data: unknown): RecoveryRow | null => {
  if (!data) return null;
  if (typeof data === 'object') return data as RecoveryRow;
  if (typeof data !== 'string') return null;

  try {
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === 'object' ? parsed as RecoveryRow : null;
  } catch {
    return null;
  }
};

export const filterRecoveryRows = (input: RecoveryRowsInput) => {
  const checklists = input.checklists.filter(
    row => matches(row.empresa_id, input.companyId)
  );
  const checklistIds = new Set(checklists.map(row => String(row.id)));
  const templateIds = new Set(checklists.map(row => String(row.template_id)));
  const values = input.values.filter(
    row =>
      matches(row.empresa_id, input.companyId) ||
      checklistIds.has(String(row.checklist_id))
  );
  const valueIds = new Set(values.map(row => String(row.id)));
  const templates = input.templates.filter(
    row => matches(row.empresa_id, input.companyId) && templateIds.has(String(row.id))
  );
  const fields = input.fields.filter(row => matches(row.empresa_id, input.companyId));
  const fieldIds = new Set(fields.map(row => String(row.id)));
  const orphanValueIds = values
    .filter(row => !checklistIds.has(String(row.checklist_id)))
    .map(row => String(row.id));
  const valuesWithoutFieldReferenceIds = values
    .filter(row => !row.campo_id || !fieldIds.has(String(row.campo_id)))
    .map(row => String(row.id));
  const markingImages = input.markingImages.filter(row => matches(row.empresa_id, input.companyId));
  const scopedItemIds = new Set([
    ...checklistIds,
    ...valueIds,
    ...templateIds,
    ...fieldIds,
  ]);

  const pendingOperations = input.pendingOperations.filter(row => {
    const itemId = String(row.itemId ?? row.item_id ?? '');
    if (scopedItemIds.has(itemId)) return true;

    const data = parsePendingData(row.data);
    return Boolean(
      data &&
      (matches(data.empresa_id, input.companyId) || matches(data.usuario_id, input.userId))
    );
  });
  const pendingImages = input.pendingImages.filter(row =>
    checklistIds.has(String(row.item_id ?? row.itemId ?? ''))
  );
  const pendingDeletions = input.pendingDeletions.filter(row => {
    const path = String(row.path ?? '');
    return (
      checklistIds.has(String(row.itemId ?? row.item_id ?? '')) ||
      path.startsWith(`${input.companyId}/`) ||
      path.includes(`/${input.companyId}/`)
    );
  });

  return {
    users: input.users.filter(
      row => matches(row.empresa_id, input.companyId) || matches(row.id, input.userId)
    ),
    companies: input.companies.filter(row => matches(row.id, input.companyId)),
    templates,
    fields,
    checklists,
    values,
    markingImages,
    pendingOperations,
    pendingImages,
    pendingDeletions,
    diagnostics: {
      orphanValueIds,
      valuesWithoutFieldReferenceIds,
    },
  };
};
